import React, { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import Card from '../../../../../components/Card/Card';
import Switch from '../../../../../components/Switch/Switch';
import { UPDATE_CHAMPIONS_LEAGUE_PHASE_LOCKS } from '../../../../../graphql/mutations';
import { CHAMPIONS_LEAGUE_SETTINGS } from '../../../../../graphql/queries';
import './PhaseControls.css';

const PhaseControls = ({ 
  currentPhaseLocks = {}, 
  onSuccess,
  onError
}) => {
  const [localPhaseLocks, setLocalPhaseLocks] = useState(currentPhaseLocks);
  const [saving, setSaving] = useState(false);

  // Opdater local state når currentPhaseLocks ændres
  React.useEffect(() => {
    setLocalPhaseLocks(currentPhaseLocks);
  }, [currentPhaseLocks]);

  const [updatePhaseLocks] = useMutation(UPDATE_CHAMPIONS_LEAGUE_PHASE_LOCKS, {
    refetchQueries: [{ query: CHAMPIONS_LEAGUE_SETTINGS }],
    onCompleted: () => {
      setSaving(false);
      if (onSuccess) onSuccess('Fase låsning opdateret!');
    },
    onError: (error) => {
      setSaving(false);
      if (onError) onError(`Fejl ved opdatering: ${error.message}`);
    }
  });

  // Sikrer at alle faser har en værdi, selv hvis de mangler i currentPhaseLocks
  const phaseLocks = {
    phase0: localPhaseLocks?.phase0 ?? false,
    phase1: localPhaseLocks?.phase1 ?? false,
    phase2: localPhaseLocks?.phase2 ?? false,
    phase2Playoff: localPhaseLocks?.phase2Playoff ?? false,
    phase2Round16: localPhaseLocks?.phase2Round16 ?? false,
    phase2Quarters: localPhaseLocks?.phase2Quarters ?? false,
    phase2Semis: localPhaseLocks?.phase2Semis ?? false,
    phase3: localPhaseLocks?.phase3 ?? false,
  };

  const handleTogglePhase = useCallback(async (phaseKey) => {
    setSaving(true);
    
    // Beregn ny state først - brug phaseLocks objektet som base
    const isCurrentlyLocked = phaseLocks[phaseKey] ?? false;
    const newPhaseLocks = {
      ...phaseLocks,
      [phaseKey]: !isCurrentlyLocked
    };

    // Opdater local state først for instant feedback
    setLocalPhaseLocks(newPhaseLocks);

    // Gem til backend
    try {
      // Fjern __typename hvis det findes
      const cleanPhaseLocks = { ...newPhaseLocks };
      delete cleanPhaseLocks.__typename;
      
      await updatePhaseLocks({
        variables: {
          phaseLocks: cleanPhaseLocks
        }
      });
    } catch (error) {
      // Revert local state ved fejl
      setLocalPhaseLocks(currentPhaseLocks);
      console.error('Error updating phase locks:', error);
    }
  }, [phaseLocks, updatePhaseLocks, currentPhaseLocks]);

  const renderPhaseRow = ({ label, phaseKey, ariaLabel, isSubphase = false, disabled = false }) => {
    const isLocked = phaseLocks[phaseKey];

    return (
      <div className={`phase-controls-item ${isSubphase ? 'phase-controls-item--subphase' : ''} ${disabled ? 'phase-controls-item--disabled' : ''}`}>
        <span>{label}</span>
        <div className="phase-controls-toggle">
          <span className="phase-controls-status">{isLocked ? 'Låst' : 'Åben'}</span>
          <Switch
            checked={!isLocked}
            onChange={() => handleTogglePhase(phaseKey)}
            color="primary"
            disabled={saving || disabled}
            ariaLabel={ariaLabel}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="admin-grid">
      
      <Card className="admin-card phase-controls-card">
        <h4>Fase Låsning</h4>
        <p>Lås eller lås op for forskellige faser i spillet.</p>
        <div className="phase-controls-sections">
          <div className="phase-controls-section">
            <div className="phase-controls-section-title">Generelle faser</div>
            <div className="phase-controls-list">
              {renderPhaseRow({
                label: 'Når denne fase er låst, kan spillere ikke gætte på vinderen af Champions League',
                phaseKey: 'phase0',
                ariaLabel: 'Fase 0 låsning',
              })}
              
              
            </div>
          </div>
          <div className="phase-controls-section">
            <div className="phase-controls-section-title">Fase 1 - Liga</div>
            <div className="phase-controls-list">
            {renderPhaseRow({
                label: 'Når denne fase er låst, kan spillere ikke ændre på ligastillingen',
                phaseKey: 'phase1',
                ariaLabel: 'Fase 1 låsning',
              })}
            </div>
          </div>

          <div className="phase-controls-section phase-controls-section--knockout">
            <div className="phase-controls-section-title">Fase 2 - Knockout</div>
            <div className="phase-controls-master">
              {renderPhaseRow({
                label: 'Når denne fase er låst, kan spillere ikke gætte på knockout-runderne',
                phaseKey: 'phase2',
                ariaLabel: 'Fase 2 overlåsning',
              })}
            </div>

            <div className="phase-controls-subgroup-label">
              Underlåse for individuelle runder
            </div>
            <div className="phase-controls-list phase-controls-subgroup">
              {renderPhaseRow({
                label: 'Playoff',
                phaseKey: 'phase2Playoff',
                ariaLabel: 'Playoff låsning',
                isSubphase: true,
                disabled: phaseLocks.phase2,
              })}
              {renderPhaseRow({
                label: 'Round of 16',
                phaseKey: 'phase2Round16',
                ariaLabel: 'Round of 16 låsning',
                isSubphase: true,
                disabled: phaseLocks.phase2,
              })}
              {renderPhaseRow({
                label: 'Quarterfinals',
                phaseKey: 'phase2Quarters',
                ariaLabel: 'Quarterfinals låsning',
                isSubphase: true,
                disabled: phaseLocks.phase2,
              })}
              {renderPhaseRow({
                label: 'Semifinals',
                phaseKey: 'phase2Semis',
                ariaLabel: 'Semifinals låsning',
                isSubphase: true,
                disabled: phaseLocks.phase2,
              })}
            </div>
            {phaseLocks.phase2 && (
              <p className="phase-controls-note">
                Knockout-overlåsning er aktiv. Lås op for fase 2 for at ændre underlåse.
              </p>
            )}
          </div>
          <div className="phase-controls-section">
            <div className="phase-controls-section-title">Fase 3 - Finalen</div>
            <div className="phase-controls-list">
              {renderPhaseRow({
                label: 'Når denne fase er låst, kan spillere ikke gætte på finalen',
                ariaLabel: 'Når denne fase er låst, kan man ikke gætte på finalen',
                phaseKey: 'phase3',
              })}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PhaseControls;
